import { Configuration, CustomFieldValue, Project } from "product-test-types";
import { getCustomFieldPublicId } from "product-test-utils";

/**
 * Ensure placeholder custom field values exist in project for enabled custom fields.
 *
 * For configuration.customFields with purpose "projectReporting" this will:
 * - Check project.customFields for an existing value matching the customField logical id.
 * - If missing, push a placeholder { customFieldDefinition, value: "" } into project.customFields.
 *
 * For configuration.customFields with purpose "itemReporting" this will:
 * - For each reporting item in project.reportingItems, check reportingItem.customFields
 *   for an existing value matching the customField logical id.
 * - If missing, push a placeholder { customFieldDefinition, value: "" } into reportingItem.customFields.
 *
 * Implementation notes:
 * - The function mutates project in-place.
 * - Placeholder entries intentionally do not include an id because they are not
 *   persisted rows yet.
 *
 * @param project - The project returned from backend (may be undefined); mutated in-place.
 * @param configuration - The Configuration containing custom field definitions (may be undefined).
 */
export function populateEmptyCustomFields(
  project: Omit<Project, "id"> | undefined,
  configuration: Configuration | undefined
) {
  for (const field of configuration?.customFields?.filter(
    (f) => f.purpose === "projectReporting" && f.enabled
  ) ?? []) {
    const customFieldValue = project?.customFields?.find(
      (cfv) =>
        getCustomFieldPublicId(cfv.customFieldDefinition) ===
        getCustomFieldPublicId(field)
    );

    if (!customFieldValue && project?.customFields) {
      project.customFields.push({
        customFieldDefinition: field,
        value: "",
      } as CustomFieldValue);
    }
  }

  for (const field of configuration?.customFields?.filter(
    (f) => f.purpose === "itemReporting" && f.enabled
  ) ?? []) {
    for (const reportingItem of project?.reportingItems ?? []) {
      const customFieldValue = reportingItem?.customFields?.find(
        (cfv) =>
          getCustomFieldPublicId(cfv.customFieldDefinition) ===
          getCustomFieldPublicId(field)
      );

      if (!customFieldValue && reportingItem?.customFields) {
        reportingItem.customFields.push({
          customFieldDefinition: field,
          value: "",
        } as CustomFieldValue);
      }
    }
  }
}
